import React, { Dispatch, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	ClientRefusalReason,
	PhoneBlacklist,
} from "../../../../../../services";
import LightInputModal from "../../../../../../components/LightInputModal";

import Content from "./components/Content";

const ClientCancelOrderModal: React.FC<ClientCancelOrderModal.Props> = ({
	addClientToBlackList,
	reasons,
	onSubmit,
	...props
}) => {
	const { t } = useTranslation();
	const [errorComment, setErrorComment] = useState<boolean>(false);
	const [errorReason, setErrorReason] = useState<boolean>(false);
	const [highlightedReason, setHighlightedReason] = useState<number | null>(
		null,
	);

	const [value, setValue] = useState<Content.Value>({
		comment: "",
		blockLevel: PhoneBlacklist.Level.WARNING,
		blockComment: "",
	});

	const modalOnSubmit = useCallback(() => {
		// Выбранная пробелом, иначе подсвеченная стрелками.
		const reason = value.reason ?? highlightedReason ?? undefined;

		setErrorReason(!reason);
		setErrorComment(addClientToBlackList && !value.blockComment);

		if (!reason || (addClientToBlackList && !value.blockComment)) return;

		onSubmit?.({ ...value, reason });
	}, [addClientToBlackList, highlightedReason, onSubmit, value]);

	return (
		<LightInputModal
			{...props}
			draggable={false}
			style={{ maxHeight: "60vh" }}
			title={
				t("pages.mainPage.pages.orders.clientCancelOrderModal.str0") ??
				""
			}
			onSubmit={modalOnSubmit}
		>
			<Content
				value={value}
				reasons={reasons}
				highlightedReason={highlightedReason}
				addClientToBlackList={addClientToBlackList}
				errorComment={errorComment}
				errorReason={errorReason}
				onChange={(next) => {
					if (next.reason) setErrorReason(false);
					setValue(next);
				}}
				onChangeHighlightedReason={(id) => {
					if (id) setErrorReason(false);
					setHighlightedReason(id);
				}}
			/>
		</LightInputModal>
	);
};

/**
 * Активные причины филиала для модалки. Пустой список значит, что причину
 * спрашивать не надо: опция выключена, причин нет или они не загрузились.
 */
export const loadClientRefusalReasons = async (
	isEnabled: boolean | undefined,
	taxiServiceId: number | undefined,
): Promise<ClientRefusalReason.Model[]> => {
	if (isEnabled === false) return [];

	try {
		return await ClientRefusalReason.getAll({
			active: true,
			...(taxiServiceId ? { taxiServiceIds: [taxiServiceId] } : {}),
		});
	} catch {
		return [];
	}
};

declare namespace ClientCancelOrderModal {
	type Value = Content.Value;

	interface Props
		extends Omit<
			LightInputModal.Props,
			"draggable" | "title" | "onSubmit"
		> {
		addClientToBlackList: boolean;
		reasons: ClientRefusalReason.Model[];

		onSubmit?: Dispatch<Value>;
	}
}

export default ClientCancelOrderModal;
